import { Alert, Button, Group, List, LoadingOverlay, Stack, Text, Textarea } from "@mantine/core";
import { IconAlertTriangle, IconBulb } from "@tabler/icons-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useImproveIdeaDescription } from "../../hooks/useImproveIdeaDescription";

interface ImproveIdeaDescriptionModalProperties {
  titulo: string;
  descricao: string;
  onApply: (suggestion: string) => void;
  onCancel: () => void;
}

const EMPTY_TEXT = "";
const SUGGESTION_TEXTAREA_MIN_ROWS = 4;
const SUGGESTION_TEXTAREA_MAX_ROWS = 30;
const ERROR_MESSAGE = "Não foi possível gerar a sugestão. Tente novamente.";

/**
 * Modal para gerar e aplicar sugestão de melhoria com IA para a descrição da ideia.
 * Agora usa modal local em vez de context modal para evitar problemas de re-render.
 * @param root0
 * @param root0.titulo
 * @param root0.descricao
 * @param root0.onApply
 * @param root0.onCancel
 */
export function ImproveIdeaDescriptionModal({
  titulo,
  descricao,
  onApply,
  onCancel
}: ImproveIdeaDescriptionModalProperties) {
  const [suggestion, setSuggestion] = useState(EMPTY_TEXT);
  const [originalSuggestion, setOriginalSuggestion] = useState(EMPTY_TEXT);
  const [orientacoes, setOrientacoes] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showEditWarning, setShowEditWarning] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const didAutoGenerate = useRef(false);
  const { mutateAsync: improveDescription } = useImproveIdeaDescription();

  const handleGenerateSuggestion = useCallback(
    async (textToImprove: string) => {
      if (!textToImprove.trim()) {
        console.warn("[ImproveIdeaDescriptionModal] Texto vazio, abortando");
        return;
      }

      setIsGenerating(true);
      setErrorMessage(null);
      setShowEditWarning(false);

      try {
        const result = await improveDescription({
          titulo,
          descricao: textToImprove
        });

        // Atualiza estados de forma consistente
        const newSuggestion = result.suggestion;
        setSuggestion(newSuggestion);
        setOriginalSuggestion(newSuggestion);
        setOrientacoes(result.orientacoes || []);
      } catch (error) {
        console.error("[ImproveIdeaDescriptionModal] Erro ao gerar sugestão:", error);
        setErrorMessage(ERROR_MESSAGE);
      } finally {
        setIsGenerating(false);
      }
    },
    [improveDescription, titulo]
  );

  const handleApply = useCallback(() => {
    const trimmedSuggestion = suggestion.trim();
    if (!trimmedSuggestion) {
      console.warn("[ImproveIdeaDescriptionModal] Suggestion vazia, não aplicando");
      return;
    }

    onApply(trimmedSuggestion);
  }, [suggestion, onApply]);

  const handleCancel = useCallback(() => {
    onCancel();
  }, [onCancel]);

  const handleSuggestionChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setSuggestion(event.currentTarget.value);
      // Esconde aviso quando usuário começa a editar
      if (showEditWarning) {
        setShowEditWarning(false);
      }
    },
    [showEditWarning]
  );

  const handleCloseEditWarning = useCallback(() => {
    setShowEditWarning(false);
  }, []);

  const handleGenerateSuggestionClick = useCallback(() => {
    const currentText = suggestion.trim();
    const originalText = originalSuggestion.trim();

    // Se o texto não foi editado (é igual ao original)
    if (currentText === originalText) {
      setShowEditWarning(true);
      return;
    }

    // Se foi editado, usa o texto editado para gerar nova sugestão
    void handleGenerateSuggestion(currentText);
  }, [suggestion, originalSuggestion, handleGenerateSuggestion]);

  useEffect(() => {
    if (didAutoGenerate.current) {
      return;
    }

    didAutoGenerate.current = true;
    void handleGenerateSuggestion(descricao);
  }, [handleGenerateSuggestion, descricao]);

  return (
    <Stack gap="md">
      <Text size="sm">
        Vamos gerar uma sugestão para melhorar seu texto. Você pode editar a sugestão abaixo e clicar em &quot;Refinar
        sugestão&quot; para gerar uma nova versão baseada nas suas alterações.
      </Text>

      {errorMessage && (
        <Alert icon={<IconAlertTriangle size={16} />} color="red">
          <Text size="sm">{errorMessage}</Text>
        </Alert>
      )}

      {showEditWarning && (
        <Alert icon={<IconAlertTriangle size={16} />} color="yellow" withCloseButton onClose={handleCloseEditWarning}>
          <Text size="sm" fw={500}>
            Para gerar uma sugestão diferente, você precisa editar o texto abaixo primeiro. Sem alterações, a IA gerará
            a mesma resposta.
          </Text>
        </Alert>
      )}

      <Stack gap="xs" pos="relative">
        <LoadingOverlay visible={isGenerating} />
        <Text fw={600} size="sm">
          Sugestão de texto
        </Text>
        <Textarea
          value={suggestion}
          onChange={handleSuggestionChange}
          autosize
          minRows={SUGGESTION_TEXTAREA_MIN_ROWS}
          maxRows={SUGGESTION_TEXTAREA_MAX_ROWS}
          placeholder={isGenerating ? "Gerando sugestão..." : "A sugestão aparecerá aqui"}
        />
      </Stack>

      {orientacoes.length > 0 && (
        <Alert icon={<IconBulb size={16} />} color="blue" variant="light">
          <Stack gap="xs">
            <Text fw={600} size="sm">
              Orientações para expandir sua ideia
            </Text>
            <List size="sm" spacing="xs">
              {orientacoes.map((orientacao, index) => (
                <List.Item key={index}>
                  <Text size="sm">{orientacao}</Text>
                </List.Item>
              ))}
            </List>
          </Stack>
        </Alert>
      )}

      <Group justify="space-between" align="center">
        <Button variant="subtle" onClick={handleGenerateSuggestionClick} disabled={isGenerating}>
          Refinar sugestão
        </Button>
        <Group>
          <Button variant="default" onClick={handleCancel}>
            Cancelar
          </Button>
          <Button onClick={handleApply} disabled={isGenerating || !suggestion.trim()}>
            Aplicar sugestão
          </Button>
        </Group>
      </Group>
    </Stack>
  );
}

export default ImproveIdeaDescriptionModal;
